import { Box } from '@mui/material'
import BoltIcon from '@mui/icons-material/Bolt'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CustomTooltip from './CustomTooltip'
import '@fontsource/rubik';

// Font/icon scale for all three pills, matching each surface's existing
// text conventions: "card" = EventCard.js (dashboard + team dashboard),
// "detail" = EventDetailCard.js (production event page).
const FONT_SIZE = {
    card: { xs: 6, md: 7, lg: '8.5px', xl: '11px' },
    detail: { xs: '9px', md: '9px', lg: '10.67px', xl: '16px' },
}
const ICON_SIZE = {
    card: { xs: '8px', md: '9px', lg: '10px', xl: '13px' },
    detail: { xs: '11px', md: '11px', lg: '13px', xl: '18px' },
}

const ACCEPTED_MESSAGE = 'Quick turn request accepted'
const PENDING_COLOR = '#8a6d1d'
const PENDING_BG = '#fdf1d6'
const PENDING_DEFAULT_MESSAGE = "Your 24-hour quick turn is pending approval."
const REJECTED_COLOR = '#b3261e'
const REJECTED_BG = '#fbe9e9'
const REJECTED_DEFAULT_MESSAGE = "Your 24-hour quick turn request wasn't approved."

// A hex color plus an alpha suffix, for a tinted pill background/border.
const withAlpha = (hex, alpha) => `${hex}${alpha}`

// Reflects the backend's quick_turn_status as a small pill (icon + label),
// with a CustomTooltip on hover explaining what it means. It never makes an
// approval decision itself, only displays whichever of the three states
// ("accepted" / "pending" / "rejected") the backend sends:
//  - "accepted" → a "Quick Turn" pill in accentColor (a lightning-bolt icon)
//  - "pending"  → an amber "Quick Turn Pending" pill; hover shows the
//    rejection note if one exists, otherwise a generic message
//  - "rejected" → a red "Quick Turn Rejected" pill; hover shows the
//    rejection note if one exists, otherwise a generic message
// Presentational only — no redux/theme/router deps — so it stays trivially
// unit-testable and can be dropped into EventCard.js (dashboard + team
// dashboard) and EventDetailCard.js (production event page) without pulling
// in either component's heavier dependency chain.
const QuickTurnBadges = ({ quickTurn, quickTurnStatus, quickTurnRejectionNote, variant = 'card', accentColor = '#00A83B' }) => {
    const isAccepted = quickTurnStatus === 'accepted'
    const isPending = quickTurnStatus === 'pending'
    const isRejected = quickTurnStatus === 'rejected'
    if (!isAccepted && !isPending && !isRejected) return null
    if (!quickTurn && !isRejected) return null

    const fontSize = FONT_SIZE[variant] || FONT_SIZE.card
    const iconSize = ICON_SIZE[variant] || ICON_SIZE.card
    const pillSx = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        borderRadius: '20px',
        fontFamily: 'Rubik',
        fontWeight: 600,
        fontSize,
        px: { xs: '6px', xl: '10px' },
        py: { xs: '2px', xl: '3px' },
        whiteSpace: 'nowrap',
    }

    return (
        <Box className="flex a-center" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
            {isAccepted && (
                <CustomTooltip title={ACCEPTED_MESSAGE} placement="top">
                    <Box
                        sx={{
                            ...pillSx,
                            backgroundColor: withAlpha(accentColor, '33'),
                            color: accentColor,
                        }}
                    >
                        <BoltIcon sx={{ fontSize: iconSize }} />
                        Quick Turn
                    </Box>
                </CustomTooltip>
            )}
            {isPending && (
                <CustomTooltip title={quickTurnRejectionNote || PENDING_DEFAULT_MESSAGE} placement="top">
                    <Box sx={{ ...pillSx, backgroundColor: PENDING_BG, color: PENDING_COLOR }}>
                        <HourglassBottomIcon sx={{ fontSize: iconSize }} />
                        Quick Turn Pending
                    </Box>
                </CustomTooltip>
            )}
            {isRejected && (
                <CustomTooltip title={quickTurnRejectionNote || REJECTED_DEFAULT_MESSAGE} placement="top">
                    <Box sx={{ ...pillSx, backgroundColor: REJECTED_BG, color: REJECTED_COLOR }}>
                        <CancelOutlinedIcon sx={{ fontSize: iconSize }} />
                        Quick Turn Rejected
                    </Box>
                </CustomTooltip>
            )}
        </Box>
    )
}

export default QuickTurnBadges
